import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Box, Alert, CircularProgress, Typography } from "@mui/material";
import ResellerDashboard from "../../components/resellers/ResellerDashboard";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

/**
 * Wave 1.1 — Route wrapper that resolves the logged-in reseller's identity
 * (GET /resellers/me) and renders the real ResellerDashboard component.
 * Replaces the former "Coming soon" placeholder.
 */
export default function ResellerDashboardPage() {
  const [resellerId, setResellerId] = useState<string | null>(null);
  const [resellerAccount, setResellerAccount] = useState<string | null>(null);
  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState(true);

  const token = localStorage.getItem("reseller_token");

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/v1/resellers/me`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) {
          const body = await response.json().catch(() => ({}));
          throw new Error(body.detail || "Failed to load reseller profile");
        }
        const data = await response.json();
        if (cancelled) return;
        setResellerId(data.id);
        setResellerAccount(data.account);
      } catch (err: any) {
        if (!cancelled) setError(err.message || "Failed to load dashboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  if (!token) {
    return <Navigate to="/reseller/login" replace />;
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !resellerId || !resellerAccount) {
    return (
      <Box p={3}>
        <Alert severity="error">
          <Typography variant="subtitle2" gutterBottom>
            Unable to load reseller dashboard
          </Typography>
          {error || "Reseller account not found for this login."}
        </Alert>
      </Box>
    );
  }

  return <ResellerDashboard resellerId={resellerId} resellerAccount={resellerAccount} />;
}
